import { defineComponent, h, withDirectives, resolveDirective } from 'vue';
import BaseComponent from './baseFormMixin.jsx';
import variableBindingMixin from './variableBindingMixin.jsx';
import utils from '../../../Shared/utils.jsx';
import methods from '../../../Shared/methods';
import { QInput, QSelect, QItem, QItemSection, QIcon, QTooltip } from 'quasar';
import { isNumericLiteral, isFlowExpressionNumericField, flowNumericKind, schemaVariableTypeHint } from './variableBinding.js';
import { storedExpressionNumericValue } from '../vuecontrols/flowv2/shared/literalNumeric.js';

import { appSettings } from '@/Shared/appSettings.js';

import TranslationContainer from '../TranslationContainer.jsx';

export default defineComponent({
    name: 'sform-number',
    mixins: [BaseComponent, variableBindingMixin],
    components: {
        TranslationContainer,
        QInput,
        QSelect,
        QItem,
        QItemSection,
        QIcon,
        QTooltip,
    },
    data: () => ({
    }),
    props: {
    },
    created() {
    },
    mounted() {
        this.loadComplete();
    },
    computed: {
        items_translated: function () {
            if (this.element.titleMap)
                return this.element.titleMap.map(item => ({
                    name: this.Translate(item.name),
                    value: item.value,
                }));
            else
                return [];
        },
        /** Flow expression numeric: string schema + AllowVariable + numeric VariableType. */
        expressionNumericField() {
            return isFlowExpressionNumericField(this.element?.schema);
        },
        expressionNumericInteger() {
            return flowNumericKind(schemaVariableTypeHint(this.element?.schema)) === 'integer';
        },
        expressionNumericRules() {
            if (!this.expressionNumericField)
                return this.rules;
            const integer = this.expressionNumericInteger;
            const asStored = (v) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : v);
            const base = (this.rules || []).map((rule) => (v) => rule(asStored(v)));
            return [
                ...base,
                (v) => (v == null || v === '' || isNumericLiteral(v, integer)) || (integer ? 'Enter a whole number' : 'Enter a number'),
            ];
        },
    },
    methods: {
        syncExpressionNumeric(value) {
            this.sync(storedExpressionNumericValue(value, this.expressionNumericInteger));
        },
        onExpressionNumericInput(value) {
            if (value != null && value !== '' && isNumericLiteral(value, this.expressionNumericInteger))
                this.syncExpressionNumeric(value);
            else
                this.sync(value);
        },
        revertExpressionNumeric() {
            this.closeVariablePicker();
            const schema = this.element?.schema;
            const restore = schema && Object.prototype.hasOwnProperty.call(schema, 'default')
                ? schema.default
                : '';
            const stored = storedExpressionNumericValue(restore, this.expressionNumericInteger);
            this.sync(typeof stored === 'number' ? stored : '');
        },
    },
    render() {
        const variableField = this.tryRenderVariableBindingField({
            class: 'c-sform-number caption c-variable-binding-select',
            labelSlot: true,
        });
        if (variableField)
            return variableField;

        const numericValue = this.itemvalue;
        const expressionText = this.expressionNumericField
            && numericValue != null
            && numericValue !== ''
            && !isNumericLiteral(numericValue, this.expressionNumericInteger);
        if (expressionText) {
            let expressionSlots = {
                append: () => (
                    this.appearance?.readonly ? null : (
                        <QIcon
                            name="mdi-close"
                            size="xs"
                            class="cursor-pointer"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.revertExpressionNumeric();
                            }}
                        >
                            <QTooltip>Revert to number</QTooltip>
                        </QIcon>
                    )
                ),
            };
            if (this.hasInputLabel)
                expressionSlots.label = () => <TranslationContainer context={this} value={this.labelText}></TranslationContainer>;
            expressionSlots = this.mergeVariableAssistSlots(expressionSlots);
            const expression = (
                <QInput
                    type="text"
                    class="c-sform-number caption"
                    bg-color="white"
                    outlined
                    dense
                    labelSlot={this.hasInputLabel}
                    modelValue={numericValue == null ? '' : String(numericValue)}
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    onUpdate:modelValue={(value) => this.onExpressionNumericInput(value)}
                    onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                    onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                >
                    {expressionSlots}
                </QInput>
            );
            return this.wrapLiteralWithVariableAssist(expression);
        }

        let input;

        let slots = {
            message: ({ message }) => {
                return <TranslationContainer context={this} value={message}></TranslationContainer>
            },
        };
        if (this.hasInputLabel)
            slots.label = () => <TranslationContainer context={this} value={this.labelText} autoOpenModal={!!this.element.titleMap}></TranslationContainer>;

        if (this.hintText)
            slots.hint = () => <TranslationContainer context={this} value={this.hintText} autoOpenModal></TranslationContainer>;

        const options = this.items_translated || [];
        if (this.element.titleMap && options.length)
            slots.option = (scope) => (
                <QItem {...scope.itemProps} {...scope.itemEvents}>
                    <QItemSection>
                        <TranslationContainer context={this} value={scope.opt.name}></TranslationContainer>
                    </QItemSection>
                </QItem>
            );

        slots = this.mergeVariableAssistSlots(slots);

        if (this.element.titleMap)
            input =
                <QSelect
                    class="c-formNumber caption"
                    popup-content-class="q-select-max-popup-height"
                    bg-color="white"
                    outlined dense labelSlot={this.hasInputLabel}
                    modelValue={this.itemvalue}
                    options={options}
                    option-label="name"
                    option-value="value"
                    map-options
                    emit-value
                    rules={this.rules} {...this.compilerErrorFieldProps}
                    onUpdate:modelValue={(value) => this.sync(value)}
                    onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                    onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                    bottom-slots
                    hide-hint
                    persistent-hint={appSettings.DebugTranslationPrefixSetting}
                >{slots}</QSelect>;
        else
            input =
                <QInput
                    class="c-sform-number caption"
                    bg-color="white"
                    type="number"
                    outlined dense labelSlot={this.hasInputLabel}
                    modelValue={this.expressionNumericField && this.itemvalue != null ? String(this.itemvalue) : this.itemvalue}
                    rules={this.expressionNumericField ? this.expressionNumericRules : this.rules} {...this.compilerErrorFieldProps}
                    step={this.expressionNumericField && this.expressionNumericInteger ? '1' : undefined}
                    onUpdate:modelValue={(value) => this.expressionNumericField
                        ? this.syncExpressionNumeric(value)
                        : this.sync(value === '' || value == null ? undefined : parseFloat(value))}
                    onFocus={() => { this._vbFocused = true; this.onFocus(); }}
                    onBlur={() => { this._vbFocused = false; this.onBlur(); }}
                    bottom-slots
                    hide-hint
                    persistent-hint={appSettings.DebugTranslationPrefixSetting}
                >{slots}</QInput>;

        if (this.appearance)
            utils.applyVue2ToVue3Props(input, this.appearance);

        if (this.directives) {
            var applyDirectives = this.directives.map(d => {
                const dirDef = d.directive || resolveDirective(d.name);

                return [dirDef, d.value, d.arg, d.modifiers];
            });
            input = withDirectives(input, applyDirectives);
        }

        return this.wrapLiteralWithVariableAssist(input);
    }
});
